import { useMemo } from "react";
import { CARD_CLASS, renderOutcome } from "../actions/card-shell";
import type { ActionCardHostProps } from "../actions/card-props";
import { useCaptcha } from "../actions/captcha";
import { NO_FIELDS } from "../actions/constants";
import type { ConfirmResponse, ResultCard } from "../actions/contract";
import { visitorTimeZone } from "../actions/dates";
import { FailureNotice } from "../actions/failure-notice";
import { ActionFieldsForm } from "../actions/fields-form";
import { buildActionSchema, normalizeMobile, type FormValues } from "../actions/form-schema";
import { DEFAULT_ACTION_LABELS, useActionLabels, type ActionLabels } from "../actions/labels";
import { useConfirmFlow } from "../actions/use-confirm-flow";
import type { LeadField, LeadFormPayload } from "../types";

export { normalizeMobile };
export type LeadValues = FormValues;

/** zod schema from the server-provided field list: field rules, the `email_or_phone` contact rule and the privacy consent. */
export function buildLeadSchema(fields: readonly LeadField[], labels: ActionLabels = DEFAULT_ACTION_LABELS, contactRule?: string) {
  return buildActionSchema(fields, labels, contactRule);
}

export interface LeadFormCardProps extends ActionCardHostProps {
  payload: LeadFormPayload;
}

function leadFallback(response: ConfirmResponse): ResultCard {
  return { kind: "lead", state: "sent", title: response.title, body: response.body };
}

/** In-chat inquiry: server fields (prefilled), contact rule, privacy consent → `confirm` → "Request sent" card. */
export function LeadFormCard({ payload, confirm, onStateChange, renderCaptcha, labels: overrides, locale, timeZone: zone, preview, showMeta }: LeadFormCardProps) {
  const labels = useActionLabels(overrides);
  const timeZone = useMemo(() => zone ?? visitorTimeZone(), [zone]);
  const flow = useConfirmFlow({ confirm, labels, timeZone, fallbackResult: leadFallback, onStateChange });
  const captcha = useCaptcha(renderCaptcha, flow.attempts);
  const outcome = renderOutcome({ result: flow.result ?? payload.result, expired: flow.expired, title: payload.title, labels, timeZone, locale, live: true });
  if (outcome) return outcome;

  const notice = flow.failure ? (
    <FailureNotice failure={flow.failure} labels={labels} submitting={flow.submitting} onRetry={() => void flow.retry({ captchaToken: captcha.take() })} />
  ) : null;
  return (
    <div className={CARD_CLASS}>
      <ActionFieldsForm
        title={payload.title}
        fields={payload.fields}
        contactRule={payload.contact_rule}
        consentText={payload.consent_text}
        privacyUrl={payload.privacy_url}
        submitLabel={payload.submit_label ?? labels.leadSubmit}
        submitting={flow.submitting}
        disabled={preview}
        labels={labels}
        rejected={flow.failure?.fields ?? NO_FIELDS}
        notice={notice}
        captcha={captcha.node}
        onSubmit={(values) => void flow.submit({ values, captchaToken: captcha.take() })}
      />
      {preview ? <p className="text-[11px] font-semibold text-fg-subtle">{labels.previewNote}</p> : null}
      {showMeta ? (
        <p className="text-[11px] text-fg-subtle">
          Action: <span className="font-mono">{payload.action_id}</span>
        </p>
      ) : null}
    </div>
  );
}
